import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";
import { useT } from "../lib/i18n";

const CTX = "https://images.unsplash.com/photo-1558417612-c8e3eb2dbc00?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const fmtPct = (n) => `${Number(n).toFixed(Number.isInteger(Number(n)) ? 0 : 1)}%`;
const fmtUsd = (n) => `$${Number(n).toFixed(2)}`;

export default function Rates() {
  const t = useT();
  useSeo({
    title: "Rates & Live Quote",
    description: "Transparent virtual card fees — see the service fee and Stripe gateway cost upfront. Try the live USD quote simulator.",
    path: "/rates",
  });
  const [rates, setRates] = useState(null);
  useEffect(() => {
    api.get("/public/rates").then((r) => setRates(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);
  const svc = rates ? fmtPct(rates.service_fee_percent) : "3.5%";
  const gwPct = rates ? fmtPct(rates.gateway_fee_percent) : "2.9%";
  const gwFix = rates ? fmtUsd(rates.gateway_fee_fixed_usd) : "$0.30";

  return (
    <div className="min-h-screen bg-[#FBF9F6]" data-testid="rates-page">
      <Header />

      <section className="bg-[#112318] text-[#FBF9F6] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase opacity-60 mb-4">{t("rates.eyebrow")}</p>
            <h1 className="serif text-5xl md:text-6xl leading-[1.05]">
              {t("rates.title_a")} <span className="italic text-[#D96C4A]">{t("rates.title_b")}</span>.
            </h1>
            <p className="mt-6 opacity-80 max-w-md">
              {t("rates.subtitle")}
            </p>
            <div className="mt-10 space-y-4 max-w-md">
              <Row k={t("rates.row.service_fee")} v={t("rates.row.service_fee_value", { pct: svc })} testId="row-service-fee" />
              <Row k={t("rates.row.gateway")} v={`${gwPct} + ${gwFix}`} testId="row-gateway-fee" />
              <Row k={t("rates.row.settlement")} v={t("rates.row.settlement_value")} />
              <Row k={t("rates.row.topup")} v={t("rates.row.topup_value")} />
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-2xl aspect-[4/3] hidden lg:block">
            <img src={CTX} alt="person paying with phone" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-16">
          <QuoteSimulator rates={rates} t={t} />
        </div>
      </section>

      <footer className="border-t border-[#E5E0D8] py-10 text-center text-sm text-[#4A5C50]">
        <div className="flex items-center justify-center gap-6 mb-3">
          <Link to="/" className="link-ul text-xs tracking-widest uppercase" data-testid="footer-home">{t("header.nav.home")}</Link>
          <Link to="/support" className="link-ul text-xs tracking-widest uppercase" data-testid="footer-support">{t("landing.footer.support")}</Link>
          <Link to="/login?role=admin" className="link-ul text-xs tracking-widest uppercase" data-testid="footer-admin-portal">{t("landing.footer.admin_portal")}</Link>
        </div>
        {t("landing.footer.copy")}
      </footer>
    </div>
  );
}

const Row = ({ k, v, testId }) => (
  <div className="flex items-center justify-between border-b border-white/10 pb-3" data-testid={testId}>
    <span className="opacity-70">{k}</span><span className="mono">{v}</span>
  </div>
);

function QuoteSimulator({ rates, t }) {
  const [amount, setAmount] = useState(100);
  const svc = rates?.service_fee_percent ?? 3.5;
  const gwPct = rates?.gateway_fee_percent ?? 2.9;
  const gwFix = rates?.gateway_fee_fixed_usd ?? 0.30;
  const a = Number(amount) || 0;
  const serviceFee = +(a * svc / 100).toFixed(2);
  const gateway = +(a * gwPct / 100 + gwFix).toFixed(2);
  const total = +(a + serviceFee + gateway).toFixed(2);
  const quick = [50, 100, 250, 500];
  return (
    <div className="bg-[#0A1F16] border border-white/10 rounded-3xl p-6 md:p-10 grid md:grid-cols-[1.3fr_1fr] gap-10 items-center shadow-2xl" data-testid="quote-simulator">
      <div>
        <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">{t("rates.sim.eyebrow")}</p>
        <h3 className="serif text-3xl md:text-4xl mt-2">{t("rates.sim.title_a")}<br /> {t("rates.sim.title_b")}</h3>
        <div className="mt-8">
          <label className="text-[10px] tracking-[0.3em] uppercase opacity-60">{t("rates.sim.amount_label")}</label>
          <div className="flex items-baseline gap-3 border-b border-white/20 focus-within:border-[#8BA888] transition pt-1">
            <span className="mono text-2xl opacity-70">$</span>
            <input
              type="number"
              min="0"
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="flex-1 bg-transparent serif text-5xl py-2 text-[#FBF9F6] focus:outline-none mono-nums"
              data-testid="quote-amount"
            />
            <span className="mono text-xs opacity-60 pb-3">USD</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {quick.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => setAmount(q)}
                className={`text-xs mono tracking-widest px-3 py-1.5 rounded-full border transition ${a === q ? "bg-[#8BA888] text-[#0A1F16] border-[#8BA888]" : "border-white/20 hover:border-[#8BA888] text-[#FBF9F6]"}`}
                data-testid={`quote-chip-${q}`}
              >
                ${q}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-white/5 rounded-2xl p-6 space-y-3" data-testid="quote-breakdown">
        <Line k={t("rates.sim.line.principal")} v={fmtUsd(a)} emphasis />
        <div className="h-px bg-white/10" />
        <Line k={t("rates.sim.line.service", { pct: fmtPct(svc) })} v={fmtUsd(serviceFee)} />
        <Line k={t("rates.sim.line.gateway", { pct: fmtPct(gwPct), fixed: fmtUsd(gwFix) })} v={fmtUsd(gateway)} />
        <div className="h-px bg-white/10" />
        <Line k={t("rates.sim.line.you_pay")} v={fmtUsd(total)} big />
        <Link to={`/register?amount=${Math.max(1, Math.round(a))}`}>
          <button
            type="button"
            className="w-full rounded-xl bg-[#8BA888] hover:bg-[#6F8E70] text-[#0A1F16] font-medium py-3 mt-3 transition"
            data-testid="quote-cta-start"
            disabled={!a}
            onClick={() => {
              api.post("/public/funnel", { kind: "cta_click", amount: Math.max(1, Math.round(a)) }).catch(() => {});
            }}
          >
            {t("rates.sim.cta", { amount: Math.max(1, Math.round(a)) })}
          </button>
        </Link>
        <p className="text-[11px] opacity-50 pt-1">{t("rates.sim.note")}</p>
      </div>
    </div>
  );
}

function Line({ k, v, emphasis, big }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className={`${emphasis ? "text-[#8BA888]" : "opacity-70"} text-sm`}>{k}</span>
      <span className={`mono ${big ? "serif text-2xl text-[#FBF9F6]" : "text-[#FBF9F6]"}`}>{v}</span>
    </div>
  );
}
